import { fromHttpPromise } from 'pulse/adapters';
import { ResolverHttpClient } from 'pulse/resolvers';
import { PromiseClient } from 'hub-http/adapters/promiseClient';
import {
  persistedTypeMetadataPromise,
  recombineAppSettingTypeMap,
  keyEntriesByFQN,
} from 'fdsr-migration-layer';
import { batchScheduler } from 'batch-promise';
import type { AppSettingsParams } from './types';
import { extractAppSetting, consumeEarlyAppSettings } from './early';

type SchedulerCall = AppSettingsParams & { httpClient: ResolverHttpClient };

const scheduler = batchScheduler()
  .read<SchedulerCall>()
  .identity(({ appSettingName }) => appSettingName)
  .partition(() => 'appSettings')
  .execute(async (calls) => {
    const callsArray = Object.values(calls);
    const httpClient = callsArray[0].httpClient as PromiseClient;
    const appSettingNames = callsArray.map((c) => c.appSettingName);

    const results = await Promise.allSettled(
      appSettingNames.map((appSettingName) =>
        persistedTypeMetadataPromise.makeFetchWithOpts({ refresh: true })({
          appSettingName,
          httpClient,
        })
      )
    );

    if (
      results[0].status === 'rejected' &&
      results.every((result) => result.status === 'rejected')
    ) {
      throw results[0].reason;
    }

    return keyEntriesByFQN(
      recombineAppSettingTypeMap(results, appSettingNames)
    );
  })
  .extract(extractAppSetting)
  .immediate();

const fetchAppSetting = (
  httpClient: ResolverHttpClient,
  params: AppSettingsParams
) =>
  consumeEarlyAppSettings(params).catch(() =>
    scheduler.queue({ httpClient, ...params })
  );

export const defaultAppSettingsResolver = fromHttpPromise(
  'appSettingsResolver',
  fetchAppSetting
);

export const __TEST_ONLY_abortAppSettingsScheduler = () => scheduler.abort();
